import React, { useMemo } from 'react';
import styled from 'styled-components';
import { STICKY_TOTAL_ROW_HEIGHT, VirtualizedTable } from 'core/components/table';
import QueryRequest from 'app/stores/ude/QueryRequest';
import { Column } from 'core/components/table/table.types';
import UdeQueryResultModel from 'app/stores/ude/UdeQueryResultModel';
import { getDimensionDisplayLabel, getDimensions } from '@kentik/ui-shared/query/query';
import { getMeasurementDimension } from '@kentik/ui-shared/query/measurements';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { startCase } from 'lodash';
import classNames from 'classnames';
import { Classes } from '@blueprintjs/core';
import { observer } from 'mobx-react';
import { RollupResult } from './util/getRollupResults';
import { renderDimensionValue, renderMetricValue } from './legendUtils';
import UdeRowActionsMenu from './UdeRowActionsMenu';
import type { AddRowFilters } from './rowFilters';
import SeriesColorLine from './SeriesColorLine';

interface Props {
  queryRequest: QueryRequest;
  measurement: Measurement;
  rollups: RollupResult[];
  onRowClick: (rowId: string, rowIndex: number, evt: React.MouseEvent) => void;
  onRowMouseEnter: (rowId: string) => void;
  onRowMouseLeave: () => void;
  onAddRowFilters?: AddRowFilters;
}

const ROW_HEIGHT = 30;

export function getUdeTableHeight(rowCount: number, { showTotalRow }: { showTotalRow: boolean }): number {
  return ROW_HEIGHT + rowCount * ROW_HEIGHT + (showTotalRow ? STICKY_TOTAL_ROW_HEIGHT : 0);
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 100%;
  overflow: auto hidden;

  .thead .th {
    white-space: nowrap;
    text-overflow: ellipsis;

    > * {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

/**
 * Indicates whether a row is rendered on the chart: the coloured line keeps the series colour at all
 * times and signals selection through line weight and alpha.
 */
const SeriesColor = observer(({ model }: { model: UdeQueryResultModel }) => {
  const { queryModel, queryRequest } = model.results;
  const isCharted = queryRequest.isRowCharted(model.id);
  return (
    <SeriesColorLine
      color={isCharted ? queryModel.getSeriesColor(model.id) : undefined}
      isSelected={queryModel.isSeriesVisible(model.id)}
    />
  );
});

function getRowClass(model: UdeQueryResultModel): string {
  const { queryModel, queryRequest } = model.results;
  const isTableOnly = queryModel.settings.chart.type === 'table';
  const isVisible = !isTableOnly && queryModel.isSeriesVisible(model.id);
  const isCharted = !isTableOnly && queryRequest.isRowCharted(model.id);
  const isDisabled = !isTableOnly && !queryRequest.isRowCharted(model.id);
  return classNames({ isTableOnly, isVisible, isCharted, [Classes.DISABLED]: isDisabled });
}

function getColumns(
  queryRequest: QueryRequest,
  measurement: Measurement,
  rollups: RollupResult[],
  onAddRowFilters?: AddRowFilters
): Column<UdeQueryResultModel>[] {
  const { query, settings } = queryRequest;
  const isTableOnly = settings.chart.type === 'table';
  const position = isTableOnly ? 'bottom' : settings.chart.legend?.position || 'bottom';
  const showRollups = isTableOnly || (settings?.chart?.legend?.showRollups ?? true);
  const cols: Column<UdeQueryResultModel>[] = [];

  if (!isTableOnly) {
    cols.push({
      name: 'color',
      label: '',
      width: position === 'right' ? 28 : 36,
      padding: '6px',
      ellipsis: false,
      sortable: false,
      resizable: false,
      colSpan: (_: unknown, totalRow: boolean) => (totalRow ? 2 : 1),
      renderer: ({ model }) => <SeriesColor model={model} />
    });
  }

  getDimensions(query).forEach((dimension) => {
    const info = getMeasurementDimension(measurement, dimension.name);
    const baseLabel = info?.Label || startCase(dimension.name);
    cols.push({
      name: `dimensions.${dimension.name}`,
      label: getDimensionDisplayLabel(dimension.name, baseLabel),
      width: 200,
      sortable: false,
      paddingRight: 12,
      renderer: ({ model }) => renderDimensionValue(dimension.name, model.get(), query, measurement)
    });
  });

  if (showRollups) {
    rollups.forEach((rollup) => {
      const baseLabel = rollup.metricInfo?.Label || rollup.metric;
      const metricLabel =
        rollup.metricInfo?.Window === 'count' ? getDimensionDisplayLabel(rollup.metric, baseLabel) : baseLabel;
      const rollupLabel = `${metricLabel} (${startCase(rollup.fn)})`;
      const hasEnumValues = Boolean(rollup.metricInfo?.Values && Object.keys(rollup.metricInfo.Values).length > 0);

      cols.push({
        name: `rollups.${rollup.key}`,
        label: rollupLabel,
        width: 150,
        sortable: false,
        align: 'right',
        paddingRight: 12,
        showTotal: !hasEnumValues,
        totalRenderer: ({ value }) =>
          renderMetricValue(rollup.metric, value as number, {
            metricInfo: rollup.metricInfo,
            measurement: query.measurement,
            forcePrefix: rollup.prefix
          }),
        renderer: ({ value }) =>
          renderMetricValue(rollup.metric, value as number, {
            metricInfo: rollup.metricInfo,
            measurement: query.measurement,
            forcePrefix: rollup.prefix
          })
      });
    });
  }

  if (onAddRowFilters) {
    cols.push({
      name: 'rowActions',
      label: '',
      width: 36,
      padding: '0px',
      ellipsis: false,
      sortable: false,
      resizable: false,
      renderer: ({ model }) => (
        <span data-row-actions>
          <UdeRowActionsMenu row={model.get()} query={query} measurement={measurement} onAddFilters={onAddRowFilters} />
        </span>
      )
    });
  }

  return cols;
}

function UdeTable({
  queryRequest,
  measurement,
  rollups,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
  onAddRowFilters
}: Props) {
  const { queryModel, results, settings } = queryRequest;
  const isTableOnly = settings.chart.type === 'table';

  const columns = useMemo(
    () => getColumns(queryRequest, measurement, rollups, onAddRowFilters),
    [measurement, queryRequest, rollups, onAddRowFilters]
  );

  const handleRowClick = (model: UdeQueryResultModel, evt: React.MouseEvent) => {
    if ((evt.target as HTMLElement).closest('[data-row-actions]')) {
      return;
    }
    if (queryModel.isRowCharted(model.id)) {
      const rowIndex = results.rows.findIndex((r) => r.id === model.id);
      onRowClick(model.id, rowIndex, evt);
    }
  };

  const handleRowMouseOver = (model: UdeQueryResultModel) => {
    onRowMouseEnter(model.id);
  };

  const handleRowMouseOut = () => {
    onRowMouseLeave();
  };

  const showRollups = isTableOnly || (settings?.chart?.legend?.showRollups ?? true);

  return (
    <Wrapper>
      <VirtualizedTable
        flexed
        collection={results}
        columns={columns}
        tabPrefId="udeTable"
        persistColumnWidths={false}
        rowHeight={ROW_HEIGHT}
        rowClass={getRowClass}
        selectOnRowClick={false}
        showTotalRow={showRollups}
        stickyTotalRow={showRollups}
        onRowClick={!isTableOnly ? handleRowClick : undefined}
        onRowMouseOver={!isTableOnly ? handleRowMouseOver : undefined}
        onRowMouseOut={!isTableOnly ? handleRowMouseOut : undefined}
      />
    </Wrapper>
  );
}

export default UdeTable;
